import { useState, useEffect, useRef, useCallback } from 'react';
import { ProductDetail } from '@/mocks/productDetails';
import { useAdminProducts } from '@/hooks/useAdminProducts';
import { supabase } from '@/lib/supabase';
import { useActivityLog } from '@/hooks/useActivityLog';
import AdminLogin from './components/AdminLogin';
import ProductTable from './components/ProductTable';
import ProductFormModal from './components/ProductFormModal';
import OrdersManager from './components/OrdersManager';
import DiscountManager from './components/DiscountManager';
import CitiesManager from './components/CitiesManager';
import BadgesManager from './components/BadgesManager';
import StoreSettings from './components/StoreSettings';
import WhatsAppSettings from './components/WhatsAppSettings';
import CustomersManager from './components/CustomersManager';
import ImportProductsModal from './components/ImportProductsModal';
import AdminAccountSettings from './components/AdminAccountSettings';
import AdminsManager, { getAllowedTabs, type AdminPermission } from './components/AdminsManager';
import DealsManager from './components/DealsManager';
import ReviewsManager from './components/ReviewsManager';
import DashboardOverview from './components/DashboardOverview';
import SalesReport from './components/SalesReport';
import PointsReport from './components/PointsReport';
import StockReport from './components/StockReport';
import GlobalSearch from './components/GlobalSearch';
import ActivityLogViewer from './components/ActivityLogViewer';
import StockNotificationsManager from './components/StockNotificationsManager';
import RestockNotifyModal from './components/RestockNotifyModal';
import DesignCustomizer from './components/DesignCustomizer';
import MediaLibrary from './components/MediaLibrary';
import ReturnsManager from './components/ReturnsManager';
import ReturnsReport from './components/ReturnsReport';

function ReportsSection({ onToast }: { onToast: (msg: string, type?: 'success' | 'error') => void }) {
  const [reportTab, setReportTab] = useState<'sales' | 'points' | 'stock' | 'returns'>('sales');

  const tabs = [
    { key: 'sales',   label: 'تقرير المبيعات',   icon: 'ri-bar-chart-2-line',   activeColor: 'border-[#1a1a1a] text-[#1a1a1a]' },
    { key: 'returns', label: 'تقرير المرتجعات',  icon: 'ri-arrow-go-back-line',  activeColor: 'border-rose-500 text-rose-600' },
    { key: 'points',  label: 'تقرير النقاط',     icon: 'ri-vip-crown-line',      activeColor: 'border-amber-500 text-amber-600' },
    { key: 'stock',   label: 'تقرير المخزون',    icon: 'ri-archive-2-line',      activeColor: 'border-emerald-500 text-emerald-600' },
  ] as const;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 px-6 pt-5 pb-0 border-b border-gray-100 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setReportTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-xl transition-all cursor-pointer whitespace-nowrap border-b-2 ${
              reportTab === tab.key
                ? tab.activeColor
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="p-6">
        {reportTab === 'sales'   && <SalesReport onToast={onToast} />}
        {reportTab === 'returns' && <ReturnsReport onToast={onToast} />}
        {reportTab === 'points'  && <PointsReport onToast={onToast} />}
        {reportTab === 'stock'   && <StockReport onToast={onToast} />}
      </div>
    </div>
  );
}

type DeleteConfirm = { id: number; name: string } | null;
type AdminTab = 'dashboard' | 'products' | 'orders' | 'returns' | 'customers' | 'reports' | 'discounts' | 'deals' | 'cities' | 'badges' | 'reviews' | 'settings' | 'whatsapp' | 'admins' | 'activity' | 'stock-notify' | 'account' | 'design' | 'media';

const TABS: { key: AdminTab; label: string; icon: string }[] = [
  { key: 'dashboard',     label: 'الرئيسية',       icon: 'ri-dashboard-3-line' },
  { key: 'products',      label: 'المنتجات',       icon: 'ri-shopping-bag-3-line' },
  { key: 'orders',        label: 'الطلبات',        icon: 'ri-file-list-3-line' },
  { key: 'returns',       label: 'المرتجعات',      icon: 'ri-arrow-go-back-line' },
  { key: 'customers',     label: 'العملاء',        icon: 'ri-group-line' },
  { key: 'reports',       label: 'التقارير',       icon: 'ri-bar-chart-2-line' },
  { key: 'discounts',     label: 'أكواد الخصم',    icon: 'ri-coupon-3-line' },
  { key: 'deals',         label: 'العروض',         icon: 'ri-flashlight-line' },
  { key: 'cities',        label: 'المدن',          icon: 'ri-map-pin-line' },
  { key: 'badges',        label: 'الشارات',        icon: 'ri-price-tag-3-line' },
  { key: 'reviews',       label: 'التقييمات',      icon: 'ri-star-line' },
  { key: 'whatsapp',      label: 'واتساب',         icon: 'ri-whatsapp-line' },
  { key: 'media',         label: 'مكتبة الصور',    icon: 'ri-image-2-line' },
  { key: 'design',        label: 'تصميم الموقع',   icon: 'ri-magic-line' },
  { key: 'settings',      label: 'الإعدادات',      icon: 'ri-settings-3-line' },
  { key: 'admins',        label: 'المشرفون',       icon: 'ri-shield-user-line' },
  { key: 'activity',      label: 'سجل النشاط',     icon: 'ri-history-line' },
  { key: 'stock-notify',  label: 'إشعارات التوفر', icon: 'ri-notification-4-line' },
  { key: 'account',       label: 'حسابي',          icon: 'ri-user-settings-line' },
];

export default function AdminPage() {
  const [authed, setAuthed] = useState<boolean | null>(null); // null = checking
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentPermission, setCurrentPermission] = useState<AdminPermission>('full');
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductDetail | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirm>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [newReviewCount, setNewReviewCount] = useState(0);
  const [stockAlertDismissed, setStockAlertDismissed] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState<{ ref: string; name: string; amount: number; city: string } | null>(null);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [stockNotifyCount, setStockNotifyCount] = useState(0);
  const [stockNotifyAlert, setStockNotifyAlert] = useState<{ productName: string; phone: string } | null>(null);
  const [stockNotifyAlertVisible, setStockNotifyAlertVisible] = useState(false);
  const [stockAlert, setStockAlert] = useState<{ name: string; stock: number; id: number } | null>(null);
  const [stockAlertVisible, setStockAlertVisible] = useState(false);
  const [outOfStockCount, setOutOfStockCount] = useState(0);
  const [restockNotifyModal, setRestockNotifyModal] = useState<{
    productId: number;
    productName: string;
    newStock: number;
  } | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const latestOrderTimeRef = useRef<string | null>(null);
  const notifiedStockIdsRef = useRef<Set<number>>(new Set());

  const { products, loading, error, addProduct, updateProduct, deleteProduct, refetch } = useAdminProducts();
  const { logActivity } = useActivityLog();

  // Filter tabs based on current user permissions
  const allowedTabs = getAllowedTabs(currentPermission);
  const visibleTabs = TABS.filter(t => allowedTabs.includes(t.key));

  // Low-stock products: stock > 0 && stock <= 5, plus out-of-stock
  const lowStockProducts = products.filter(p => (p.stock ?? 0) <= 5);
  const outOfStockProducts = products.filter(p => (p.stock ?? 0) === 0);
  const showStockAlert = lowStockProducts.length > 0 && !stockAlertDismissed;

  // Check Supabase Auth session on mount
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isAdmin = session?.user?.user_metadata?.role === 'admin';
      setAuthed(isAdmin);
      if (session?.user?.email) setCurrentUserEmail(session.user.email);
      const perm = (session?.user?.user_metadata?.permissions ?? 'full') as AdminPermission;
      setCurrentPermission(perm);
      // Set default tab based on permissions
      const allowed = getAllowedTabs(perm);
      if (!allowed.includes('dashboard')) {
        setActiveTab((allowed[0] as AdminTab) ?? 'account');
      }
    };
    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const isAdmin = session?.user?.user_metadata?.role === 'admin';
      setAuthed(isAdmin);
      if (session?.user?.email) setCurrentUserEmail(session.user.email);
      const perm = (session?.user?.user_metadata?.permissions ?? 'full') as AdminPermission;
      setCurrentPermission(perm);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Play a soft notification beep
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.4);
    } catch { /* silent */ }
  }, []);

  // Realtime subscription for new orders
  useEffect(() => {
    if (!authed) return;

    // Get the latest order time on mount to avoid false alerts
    const initLatestTime = async () => {
      const { data } = await supabase
        .from('orders')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      latestOrderTimeRef.current = data?.created_at ?? new Date().toISOString();
    };
    initLatestTime();

    const channel = supabase
      .channel('admin-new-orders')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const row = payload.new as {
            order_ref: string; name: string; final_price: number;
            city: string; created_at: string;
          };
          // Only alert for orders newer than what we had on mount
          if (
            latestOrderTimeRef.current &&
            new Date(row.created_at) <= new Date(latestOrderTimeRef.current)
          ) return;

          latestOrderTimeRef.current = row.created_at;
          setNewOrderAlert({
            ref: row.order_ref,
            name: row.name,
            amount: Number(row.final_price),
            city: row.city,
          });
          setNewOrderCount(c => c + 1);
          setAlertDismissed(false);
          playNotificationSound();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [authed, playNotificationSound]);

  // Fetch initial pending stock notifications count
  useEffect(() => {
    if (!authed) return;
    const fetchCount = async () => {
      const { count } = await supabase
        .from('stock_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setStockNotifyCount(count ?? 0);
    };
    fetchCount();
  }, [authed]);

  // Realtime subscription for new stock notification subscriptions
  useEffect(() => {
    if (!authed) return;

    const notifyChannel = supabase
      .channel('admin-stock-notify-watch')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'stock_notifications' },
        (payload) => {
          const row = payload.new as { product_name: string; whatsapp: string; phone: string };
          setStockNotifyCount(c => c + 1);
          setStockNotifyAlert({
            productName: row.product_name,
            phone: row.whatsapp || row.phone,
          });
          setStockNotifyAlertVisible(true);
          // Play soft tone
          try {
            if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
            const ctx = audioCtxRef.current;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(660, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15);
            gain.gain.setValueAtTime(0.2, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.4);
          } catch { /* silent */ }
          setTimeout(() => setStockNotifyAlertVisible(false), 7000);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(notifyChannel); };
  }, [authed]);

  // Realtime subscription for stock changes
  useEffect(() => {
    if (!authed) return;

    const stockChannel = supabase
      .channel('admin-stock-watch')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'products' },
        (payload) => {
          const row = payload.new as { id: number; name_ar: string; stock: number };
          const oldRow = payload.old as { stock: number };
          const newStock = Number(row.stock ?? 0);
          const oldStock = Number(oldRow.stock ?? 0);

          // Only alert if stock just hit 0 or just dropped to ≤ 3 (and wasn't already notified)
          const justOutOfStock = newStock === 0 && oldStock > 0;
          const justLowStock = newStock > 0 && newStock <= 3 && oldStock > 3;

          if ((justOutOfStock || justLowStock) && !notifiedStockIdsRef.current.has(row.id)) {
            notifiedStockIdsRef.current.add(row.id);
            setStockAlert({ name: row.name_ar, stock: newStock, id: row.id });
            setStockAlertVisible(true);
            if (justOutOfStock) {
              setOutOfStockCount(c => c + 1);
            }
            // Play a different tone for stock alert
            try {
              if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
              const ctx = audioCtxRef.current;
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.type = 'sine';
              osc.frequency.setValueAtTime(440, ctx.currentTime);
              osc.frequency.exponentialRampToValueAtTime(330, ctx.currentTime + 0.2);
              gain.gain.setValueAtTime(0.25, ctx.currentTime);
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
              osc.start(ctx.currentTime);
              osc.stop(ctx.currentTime + 0.5);
            } catch { /* silent */ }
            // Auto-dismiss after 8 seconds
            setTimeout(() => setStockAlertVisible(false), 8000);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(stockChannel); };
  }, [authed]);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3500);
  };

  const handleSave = async (data: Omit<ProductDetail, 'id'>) => {
    setActionLoading(true);
    let ok = false;
    if (editProduct) {
      const prevStock = editProduct.stock ?? 0;
      ok = await updateProduct(editProduct.id, data);
      showToast(ok ? 'تم تحديث المنتج بنجاح ✓' : 'حدث خطأ أثناء التحديث', ok ? 'success' : 'error');
      if (ok) {
        logActivity({ actionType: 'product_update', entityType: 'product', entityId: editProduct.id, entityName: data.nameAr, adminEmail: currentUserEmail, adminName: currentUserEmail.split('@')[0] });
        // Check if restocked from 0 → offer bulk notify
        const newStock = data.stock ?? 0;
        if (prevStock === 0 && newStock > 0) {
          const { count } = await (await import('@/lib/supabase')).supabase
            .from('stock_notifications')
            .select('*', { count: 'exact', head: true })
            .eq('product_id', editProduct.id)
            .eq('status', 'pending');
          if ((count ?? 0) > 0) {
            setRestockNotifyModal({
              productId: editProduct.id,
              productName: data.nameAr,
              newStock,
            });
          }
        }
      }
    } else {
      ok = await addProduct(data);
      showToast(ok ? 'تم إضافة المنتج بنجاح ✓' : 'حدث خطأ أثناء الإضافة', ok ? 'success' : 'error');
      if (ok) logActivity({ actionType: 'product_add', entityType: 'product', entityName: data.nameAr, adminEmail: currentUserEmail, adminName: currentUserEmail.split('@')[0] });
    }
    setActionLoading(false);
    if (ok) {
      setModalOpen(false);
      setEditProduct(null);
    }
  };

  const handleEdit = (product: ProductDetail) => {
    setEditProduct(product);
    setModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setActionLoading(true);
    const ok = await deleteProduct(deleteConfirm.id);
    showToast(ok ? 'تم حذف المنتج' : 'حدث خطأ أثناء الحذف', ok ? 'success' : 'error');
    if (ok) logActivity({ actionType: 'product_delete', entityType: 'product', entityId: deleteConfirm.id, entityName: deleteConfirm.name, adminEmail: currentUserEmail, adminName: currentUserEmail.split('@')[0] });
    setActionLoading(false);
    setDeleteConfirm(null);
  };

  const filtered = products.filter(p =>
    p.nameAr.includes(search) || p.category.toLowerCase().includes(search.toLowerCase())
  );

  const logout = async () => {
    await supabase.auth.signOut();
    setAuthed(false);
  };

  const activeTabInfo = TABS.find(t => t.key === activeTab)!;

  // Still checking session
  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#f5f5f3] flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">جاري التحقق من الجلسة...</p>
        </div>
      </div>
    );
  }

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#f5f5f3]" dir="rtl">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
              <i className="ri-shield-keyhole-line text-white text-sm"></i>
            </div>
            <span className="font-bold text-[#1a1a1a] text-sm hidden sm:block">لوحة تحكم إبداع الطائف</span>
            <span className="font-bold text-[#1a1a1a] text-sm sm:hidden">الأدمن</span>
            {currentPermission !== 'full' && (
              <span className={`hidden sm:flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                currentPermission === 'orders'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                <i className={`text-xs ${currentPermission === 'orders' ? 'ri-file-list-3-line' : 'ri-shopping-bag-3-line'}`}></i>
                {currentPermission === 'orders' ? 'مشرف طلبات' : 'مشرف منتجات'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <GlobalSearch onNavigate={(tab) => setActiveTab(tab as AdminTab)} />

            {/* Stock alert bell */}
            {outOfStockCount > 0 && (
              <button
                onClick={() => {
                  setActiveTab('products');
                  setOutOfStockCount(0);
                }}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 transition-colors cursor-pointer"
                title="منتجات نفد مخزونها"
              >
                <i className="ri-alarm-warning-line text-base"></i>
                <span className="absolute -top-0.5 -left-0.5 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[9px] font-black rounded-full animate-bounce">
                  {outOfStockCount > 9 ? '9+' : outOfStockCount}
                </span>
              </button>
            )}

            {/* New order bell */}
            <button
              onClick={() => {
                setActiveTab('orders');
                setNewOrderCount(0);
                setNewOrderAlert(null);
              }}
              className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer"
              title="الطلبات الجديدة"
            >
              <i className={`ri-notification-3-line text-base ${newOrderCount > 0 ? 'text-amber-500' : ''}`}></i>
              {newOrderCount > 0 && (
                <span className="absolute -top-0.5 -left-0.5 w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[9px] font-black rounded-full animate-bounce">
                  {newOrderCount > 9 ? '9+' : newOrderCount}
                </span>
              )}
            </button>

            <a href="/" className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1.5 cursor-pointer px-2 py-2">
              <i className="ri-store-2-line text-sm"></i>
              <span className="hidden sm:block">عرض المتجر</span>
            </a>
            <button
              onClick={logout}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap px-2 py-2"
            >
              <i className="ri-logout-box-r-line text-sm"></i>
              <span className="hidden sm:block">خروج</span>
            </button>
          </div>
        </div>
      </header>

      {/* New Order Popup Banner */}
      {newOrderAlert && !alertDismissed && (
        <div className="fixed bottom-6 right-6 z-50 w-full max-w-sm" dir="rtl">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            {/* Green accent top */}
            <div className="h-1 bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400"></div>
            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* Animated icon */}
                <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 relative">
                  <i className="ri-shopping-bag-3-line text-green-600 text-lg"></i>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-ping"></span>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"></span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-black text-[#1a1a1a]">طلب جديد!</p>
                    <span className="text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                      الآن
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    <span className="font-bold text-[#1a1a1a]">{newOrderAlert.name}</span>
                    {' · '}{newOrderAlert.city}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs font-black text-[#1a1a1a]">
                      #{newOrderAlert.ref}
                    </span>
                    <span className="text-gray-300 text-xs">·</span>
                    <span className="text-sm font-black text-green-600">
                      {newOrderAlert.amount.toLocaleString('ar-SA')} ر.س
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => { setAlertDismissed(true); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>

              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setActiveTab('orders');
                    setNewOrderCount(0);
                    setAlertDismissed(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#1a1a1a] hover:bg-[#333] text-white text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line text-sm"></i>
                  عرض الطلبات
                </button>
                <button
                  onClick={() => {
                    window.open(`/admin/order/${newOrderAlert.ref}`, '_blank');
                    setAlertDismissed(true);
                  }}
                  className="flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line text-sm"></i>
                  تفاصيل
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Alert Popup */}
      {stockAlert && stockAlertVisible && (
        <div className="fixed bottom-6 left-6 z-50 w-full max-w-sm" dir="rtl">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div className={`h-1 ${stockAlert.stock === 0 ? 'bg-gradient-to-r from-red-400 via-red-500 to-rose-500' : 'bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500'}`}></div>
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 relative ${stockAlert.stock === 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
                  <i className={`text-lg ${stockAlert.stock === 0 ? 'ri-error-warning-line text-red-500' : 'ri-alarm-warning-line text-amber-500'}`}></i>
                  <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full animate-ping ${stockAlert.stock === 0 ? 'bg-red-400' : 'bg-amber-400'}`}></span>
                  <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${stockAlert.stock === 0 ? 'bg-red-500' : 'bg-amber-500'}`}></span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className={`text-sm font-black ${stockAlert.stock === 0 ? 'text-red-700' : 'text-amber-700'}`}>
                      {stockAlert.stock === 0 ? 'نفد المخزون!' : 'مخزون منخفض!'}
                    </p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                      stockAlert.stock === 0
                        ? 'text-red-600 bg-red-50 border-red-200'
                        : 'text-amber-600 bg-amber-50 border-amber-200'
                    }`}>
                      {stockAlert.stock === 0 ? 'نفد' : `${stockAlert.stock} قطعة`}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate font-medium">{stockAlert.name}</p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {stockAlert.stock === 0
                      ? 'هذا المنتج غير متاح للشراء الآن'
                      : 'الكمية المتبقية منخفضة جداً'}
                  </p>
                </div>
                <button
                  onClick={() => setStockAlertVisible(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setActiveTab('products');
                    setStockAlertVisible(false);
                  }}
                  className={`flex-1 flex items-center justify-center gap-1.5 text-white text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                    stockAlert.stock === 0 ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  <i className="ri-shopping-bag-3-line text-sm"></i>
                  تحديث المخزون
                </button>
                <button
                  onClick={() => setStockAlertVisible(false)}
                  className="flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  تجاهل
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Notify Alert Popup */}
      {stockNotifyAlert && stockNotifyAlertVisible && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4" dir="rtl">
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
            <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500"></div>
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0 relative">
                  <i className="ri-notification-4-fill text-amber-500 text-lg"></i>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-ping"></span>
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full"></span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-black text-[#1a1a1a]">اشتراك جديد في إشعار التوفر!</p>
                    <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      الآن
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="font-bold text-[#1a1a1a]">{stockNotifyAlert.phone}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    طلب إشعار عند توفر: <span className="font-semibold text-gray-600">{stockNotifyAlert.productName}</span>
                  </p>
                </div>
                <button
                  onClick={() => setStockNotifyAlertVisible(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setActiveTab('stock-notify');
                    setStockNotifyAlertVisible(false);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-[#1a1a1a] hover:bg-[#333] text-white text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-notification-4-line text-sm"></i>
                  عرض الإشعارات
                </button>
                <button
                  onClick={() => setStockNotifyAlertVisible(false)}
                  className="flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-3 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  تجاهل
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col gap-1 w-52 flex-shrink-0">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-2">القائمة</p>
          {visibleTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap text-right ${
                activeTab === tab.key ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              <i className={`${tab.icon} text-base flex-shrink-0`}></i>
              <span className="flex-1">{tab.label}</span>
              {tab.key === 'reviews' && newReviewCount > 0 && (
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-amber-400 text-black">
                  {newReviewCount}
                </span>
              )}
              {tab.key === 'products' && lowStockProducts.length > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                  activeTab === 'products' ? 'bg-red-400 text-white' : 'bg-red-500 text-white'
                }`}>
                  {lowStockProducts.length}
                </span>
              )}
              {tab.key === 'stock-notify' && stockNotifyCount > 0 && (
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center bg-amber-400 text-black animate-pulse">
                  {stockNotifyCount > 9 ? '9+' : stockNotifyCount}
                </span>
              )}
            </button>
          ))}
        </aside>

        {/* Mobile Tab Bar */}
        <div className="lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm font-bold text-[#1a1a1a] cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <i className={`${activeTabInfo.icon} text-base`}></i>
              {activeTabInfo.label}
            </div>
            <i className={`${mobileMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400`}></i>
          </button>
          {mobileMenuOpen && (
            <div className="mt-1 bg-white border border-gray-100 rounded-xl overflow-hidden">
              {visibleTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors cursor-pointer text-right border-b border-gray-50 last:border-0 ${
                    activeTab === tab.key ? 'bg-[#1a1a1a] text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  <span className="flex-1">{tab.label}</span>
                  {tab.key === 'reviews' && newReviewCount > 0 && (
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-amber-400 text-black min-w-[18px] text-center">
                      {newReviewCount}
                    </span>
                  )}
                  {tab.key === 'products' && lowStockProducts.length > 0 && (
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-red-500 text-white min-w-[18px] text-center">
                      {lowStockProducts.length}
                    </span>
                  )}
                  {tab.key === 'stock-notify' && stockNotifyCount > 0 && (
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-amber-400 text-black min-w-[18px] text-center">
                      {stockNotifyCount > 9 ? '9+' : stockNotifyCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main Content */}
        <main className="flex-1 min-w-0">

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <DashboardOverview
              onNavigate={(tab) => setActiveTab(tab as AdminTab)}
              lowStockCount={lowStockProducts.length}
              newReviewCount={newReviewCount}
            />
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <>
              {/* ── Low-stock alert banner ── */}
              {showStockAlert && (
                <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
                  <div className="flex items-start gap-3 px-5 py-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0 mt-0.5">
                      <i className="ri-alarm-warning-line text-amber-600 text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-amber-800">
                        تنبيه المخزون — {lowStockProducts.length} {lowStockProducts.length === 1 ? 'منتج' : 'منتجات'} تحتاج انتباهك
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        {outOfStockProducts.length > 0 && (
                          <span className="font-semibold text-red-600">{outOfStockProducts.length} نفد مخزونه تماماً · </span>
                        )}
                        {lowStockProducts.filter(p => (p.stock ?? 0) > 0).length > 0 && (
                          <span>{lowStockProducts.filter(p => (p.stock ?? 0) > 0).length} كمية محدودة (5 قطع أو أقل)</span>
                        )}
                      </p>
                      {/* Product chips */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {lowStockProducts.slice(0, 6).map(p => {
                          const s = p.stock ?? 0;
                          return (
                            <button
                              key={p.id}
                              onClick={() => {
                                setEditProduct(p);
                                setModalOpen(true);
                              }}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border cursor-pointer transition-all hover:scale-105 ${
                                s === 0
                                  ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                                  : 'bg-white border-amber-200 text-amber-800 hover:bg-amber-100'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s === 0 ? 'bg-red-400' : 'bg-amber-400 animate-pulse'}`}></span>
                              <span className="max-w-[120px] truncate">{p.nameAr}</span>
                              <span className={`font-bold ${s === 0 ? 'text-red-600' : 'text-amber-700'}`}>
                                {s === 0 ? 'نفد' : `${s}`}
                              </span>
                            </button>
                          );
                        })}
                        {lowStockProducts.length > 6 && (
                          <span className="inline-flex items-center text-xs text-amber-600 px-2 py-1">
                            +{lowStockProducts.length - 6} أخرى
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setStockAlertDismissed(true)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-amber-400 hover:text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer flex-shrink-0"
                      title="إخفاء"
                    >
                      <i className="ri-close-line text-base"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'إجمالي المنتجات', value: products.length, icon: 'ri-shopping-bag-3-line' },
                  { label: 'أقل سعر', value: products.length ? `${Math.min(...products.map(p => p.price)).toLocaleString('ar-SA')} ر.س` : '—', icon: 'ri-price-tag-3-line' },
                  { label: 'أعلى سعر', value: products.length ? `${Math.max(...products.map(p => p.price)).toLocaleString('ar-SA')} ر.س` : '—', icon: 'ri-arrow-up-circle-line' },
                  { label: 'الفئات', value: new Set(products.map(p => p.category)).size, icon: 'ri-layout-grid-line' },
                ].map(stat => (
                  <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg mb-3">
                      <i className={`${stat.icon} text-[#1a1a1a] text-sm`}></i>
                    </div>
                    <p className="text-lg font-bold text-[#1a1a1a]">{stat.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl border border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <h2 className="font-bold text-[#1a1a1a] text-base">المنتجات</h2>
                    {/* DB badge */}
                    <span className="flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      <i className="ri-database-2-line text-xs"></i>
                      Supabase
                    </span>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="flex-1 sm:w-52 relative">
                      <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="بحث عن منتج..."
                        className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
                      />
                    </div>
                    <button
                      onClick={() => refetch()}
                      className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      title="تحديث"
                    >
                      <i className="ri-refresh-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setImportModalOpen(true)}
                      className="border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                      title="نقل المنتجات من mock data"
                    >
                      <i className="ri-upload-cloud-2-line"></i>
                      <span className="hidden sm:inline">استيراد</span>
                    </button>
                    <button
                      onClick={() => { setEditProduct(null); setModalOpen(true); }}
                      className="bg-[#1a1a1a] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#333] transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                      منتج جديد
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
                      <p className="text-sm text-gray-400">جاري تحميل المنتجات...</p>
                    </div>
                  ) : error ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full">
                        <i className="ri-error-warning-line text-red-400 text-xl"></i>
                      </div>
                      <p className="text-sm text-gray-500">{error}</p>
                      <button onClick={refetch} className="text-sm text-[#1a1a1a] font-medium hover:underline cursor-pointer">
                        إعادة المحاولة
                      </button>
                    </div>
                  ) : (
                    <ProductTable
                      products={filtered}
                      onEdit={handleEdit}
                      onDelete={id => {
                        const p = products.find(x => x.id === id);
                        if (p) setDeleteConfirm({ id, name: p.nameAr });
                      }}
                      onUpdateStock={async (id, stock, prevStock) => {
                        const ok = await updateProduct(id, { stock });
                        if (ok) {
                          showToast(`تم تحديث المخزون إلى ${stock} قطعة ✓`);
                          // If product was out of stock and now restocked → offer bulk notify
                          if (prevStock === 0 && stock > 0) {
                            const product = products.find(p => p.id === id);
                            if (product) {
                              // Check if there are pending subscribers
                              const { count } = await (await import('@/lib/supabase')).supabase
                                .from('stock_notifications')
                                .select('*', { count: 'exact', head: true })
                                .eq('product_id', id)
                                .eq('status', 'pending');
                              if ((count ?? 0) > 0) {
                                setRestockNotifyModal({
                                  productId: id,
                                  productName: product.nameAr,
                                  newStock: stock,
                                });
                              }
                            }
                          }
                        } else {
                          showToast('حدث خطأ أثناء تحديث المخزون', 'error');
                        }
                        return ok;
                      }}
                      onUpdatePrice={async (id, price, originalPrice) => {
                        const ok = await updateProduct(id, { price, originalPrice });
                        if (ok) showToast(`تم تحديث السعر إلى ${price.toLocaleString('ar-SA')} ر.س ✓`);
                        else showToast('حدث خطأ أثناء تحديث السعر', 'error');
                        return ok;
                      }}
                      onUpdateName={async (id, nameAr) => {
                        const ok = await updateProduct(id, { nameAr });
                        if (ok) showToast(`تم تحديث اسم المنتج ✓`);
                        else showToast('حدث خطأ أثناء تحديث الاسم', 'error');
                        return ok;
                      }}
                      onUpdateCategory={async (id, category) => {
                        const ok = await updateProduct(id, { category });
                        if (ok) showToast(`تم تغيير الفئة إلى ${category} ✓`);
                        else showToast('حدث خطأ أثناء تحديث الفئة', 'error');
                        return ok;
                      }}
                    />
                  )}
                </div>

                <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-between">
                  <p className="text-xs text-gray-400">{filtered.length} منتج</p>
                  <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <i className="ri-cloud-line text-sm"></i>
                    <span>محفوظ في قاعدة البيانات</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'orders' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-file-list-3-line text-base"></i>
                إدارة الطلبات
              </h2>
              <OrdersManager
                onToast={showToast}
                currentUserEmail={currentUserEmail}
                currentUserName={currentUserEmail.split('@')[0]}
              />
            </div>
          )}

          {activeTab === 'returns' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-arrow-go-back-line text-base"></i>
                إدارة المرتجعات
              </h2>
              <ReturnsManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'customers' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-group-line text-base"></i>
                العملاء المسجلون
              </h2>
              <CustomersManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'reports' && (
            <ReportsSection onToast={showToast} />
          )}

          {activeTab === 'discounts' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-coupon-3-line text-base"></i>
                أكواد الخصم
              </h2>
              <DiscountManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'deals' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-flashlight-line text-base"></i>
                العروض والتخفيضات
              </h2>
              <DealsManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'cities' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-map-pin-line text-base"></i>
                مدن التوصيل
              </h2>
              <CitiesManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'badges' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-base"></i>
                شارات المنتجات
              </h2>
              <BadgesManager onToast={showToast} />
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-star-line text-base"></i>
                تقييمات العملاء
                {newReviewCount > 0 && (
                  <span className="flex items-center gap-1 bg-amber-400 text-black text-[11px] font-black px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-black/30 animate-pulse inline-block"></span>
                    {newReviewCount} جديد
                  </span>
                )}
              </h2>
              <ReviewsManager
                onToast={showToast}
                onNewReviewCount={setNewReviewCount}
              />
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <WhatsAppSettings onToast={showToast} />
            </div>
          )}

          {activeTab === 'media' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <MediaLibrary onToast={showToast} />
            </div>
          )}

          {activeTab === 'design' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <DesignCustomizer onToast={showToast} />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-settings-3-line text-base"></i>
                إعدادات المتجر
              </h2>
              <StoreSettings onToast={showToast} />
            </div>
          )}

          {activeTab === 'admins' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-shield-user-line text-base"></i>
                إدارة المشرفين
              </h2>
              <AdminsManager currentUserEmail={currentUserEmail} onToast={showToast} />
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-history-line text-base"></i>
                سجل النشاط
              </h2>
              <ActivityLogViewer />
            </div>
          )}

          {activeTab === 'stock-notify' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" ref={(el) => { if (el) setStockNotifyCount(0); }}>
              <StockNotificationsManager />
            </div>
          )}

          {activeTab === 'account' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-[#1a1a1a] text-base mb-5 flex items-center gap-2">
                <i className="ri-user-settings-line text-base"></i>
                حساب المدير
              </h2>
              <AdminAccountSettings onToast={showToast} />
            </div>
          )}
        </main>
      </div>

      {/* Import Products Modal */}
      {importModalOpen && (
        <ImportProductsModal
          existingIds={products.map(p => p.id)}
          onClose={() => setImportModalOpen(false)}
          onDone={(count) => {
            if (count > 0) {
              refetch();
              showToast(`تم استيراد ${count} منتج بنجاح ✓`);
            }
          }}
        />
      )}

      {/* Product Form Modal */}
      {modalOpen && (
        <ProductFormModal
          product={editProduct}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditProduct(null); }}
          saving={actionLoading}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف المنتج</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              هل أنت متأكد من حذف &quot;{deleteConfirm.name}&quot;؟
              <br />لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={actionLoading}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? <i className="ri-loader-4-line animate-spin"></i> : null}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restock Notify Modal */}
      {restockNotifyModal && (
        <RestockNotifyModal
          productId={restockNotifyModal.productId}
          productName={restockNotifyModal.productName}
          newStock={restockNotifyModal.newStock}
          onClose={() => setRestockNotifyModal(null)}
          onDone={(count) => {
            if (count > 0) showToast(`تم إشعار ${count} مشترك بتوفر المنتج ✓`);
            setRestockNotifyModal(null);
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className={`text-white text-sm px-5 py-3 rounded-xl flex items-center gap-2 ${toastType === 'error' ? 'bg-red-500' : 'bg-[#1a1a1a]'}`}>
            <i className={toastType === 'error' ? 'ri-error-warning-line text-red-200' : 'ri-checkbox-circle-line text-green-400'}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
